import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { legalAgreementsAdminApi } from '@/lib/pillarApi';
import {
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
    type LegalAgreementDocumentReference,
    type LegalAgreementImpact,
    type LegalAgreementImpactChange,
    type LegalAgreementImpactRequest,
} from '@pillar-portal/api-types';
import { LegalDocumentPurpose } from '@pillar-portal/database/dist/frontend';

interface LegalAgreementImpactConfirmationProps {
    /** The change to preview; the dialog is open while this is not null. */
    readonly request: LegalAgreementImpactRequest | null;
    readonly title: string;
    readonly confirmLabel: string;
    readonly isConfirming: boolean;
    readonly onConfirm: (impact: LegalAgreementImpact) => void;
    readonly onCancel: () => void;
    /** Extra controls under the preview, e.g. choosing a replacement. */
    readonly children?: (impact: LegalAgreementImpact) => ReactNode;
    /** Blocks confirming even after the preview loads. */
    readonly confirmBlocked?: (impact: LegalAgreementImpact) => boolean;
}

function documentLabel(legalDocument: LegalAgreementDocumentReference): string {
    return `${legalDocument.legal_document_title} (${legalDocument.legal_document_version})`;
}

function plural(count: number, singular: string, pluralForm: string): string {
    return `${count} ${count === 1 ? singular : pluralForm}`;
}

function organizationsLabel(change: LegalAgreementImpactChange): string {
    const named = change.sample_organization_names.join(', ');
    const more = change.organization_count - change.sample_organization_names.length;
    return `${plural(change.organization_count, 'Organization', 'Organizations')} (${named}${
        more > 0 ? `, and ${more} more` : ''
    })`;
}

/** One plain sentence per change, e.g. who must now accept which agreement. */
export function describeLegalAgreementImpactChange(change: LegalAgreementImpactChange): string {
    const organizations = organizationsLabel(change);
    if (change.next_legal_document === null) {
        return change.previous_legal_document === null
            ? `${organizations} change.`
            : `${organizations} will no longer require ${documentLabel(change.previous_legal_document)}.`;
    }
    const next = documentLabel(change.next_legal_document);
    if (change.legal_document_purpose === LegalDocumentPurpose.ORGANIZATION_AGREEMENT) {
        return `${organizations} will be asked to sign ${next}; ${plural(change.organizations_to_sign, 'has', 'have')} not signed it yet.`;
    }
    const switchFrom =
        change.previous_legal_document === null
            ? ''
            : ` instead of ${documentLabel(change.previous_legal_document)}`;
    return `${organizations} will require ${next}${switchFrom}; ${plural(change.users_to_accept, 'user', 'users')} will be asked to accept it.`;
}

/**
 * Confirms a legal-agreement change after showing whom it affects. The change
 * itself runs only on confirm.
 */
export function LegalAgreementImpactConfirmation({
    request,
    title,
    confirmLabel,
    isConfirming,
    onConfirm,
    onCancel,
    children,
    confirmBlocked,
}: LegalAgreementImpactConfirmationProps) {
    const impactQuery = useQuery({
        queryKey: ['legalAgreementImpact', request],
        queryFn: async () => {
            if (request === null) {
                throw new Error('No legal agreement change to preview');
            }
            return await legalAgreementsAdminApi.previewImpact(request);
        },
        enabled: request !== null,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
    const impact = impactQuery.data;
    let message: ReactNode;
    if (impactQuery.isError) {
        message = 'Unable to preview who this change affects.';
    } else if (impact === undefined) {
        message = 'Checking who this change affects...';
    } else {
        message = `Effective ${formatTimestampForMerchantUi(impact.evaluated_at, SYSTEM_DISPLAY_TIME_ZONE)}.`;
    }
    return (
        <ConfirmationModal
            open={request !== null}
            onOpenChange={(open) => {
                if (!open) {
                    onCancel();
                }
            }}
            title={title}
            message={message}
            confirmLabel={confirmLabel}
            isLoading={isConfirming}
            confirmDisabled={
                impact === undefined || (confirmBlocked !== undefined && confirmBlocked(impact))
            }
            onConfirm={() => {
                if (impact !== undefined) {
                    onConfirm(impact);
                }
            }}
        >
            {impact === undefined ? null : (
                <div className="space-y-2 text-sm" aria-label="Change impact">
                    {impact.changes.length === 0 ? (
                        <p>No Organization&apos;s agreements change.</p>
                    ) : (
                        <ul className="list-disc space-y-1 pl-5">
                            {impact.changes.map((change) => (
                                <li
                                    key={`${change.legal_document_title}|${change.previous_legal_document?.legal_document_uuid ?? ''}|${change.next_legal_document?.legal_document_uuid ?? ''}`}
                                >
                                    {describeLegalAgreementImpactChange(change)}
                                </li>
                            ))}
                        </ul>
                    )}
                    {impact.leaves_no_platform_default ? (
                        <p role="alert" className="font-medium text-red-700">
                            After this, no platform-default user agreement is in force.
                        </p>
                    ) : null}
                    {children === undefined ? null : children(impact)}
                </div>
            )}
        </ConfirmationModal>
    );
}
